import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TagManager from 'react-gtm-module';
import { Deal } from 'src/types';

type Props = {
  deal: Deal;
};

function formatExpiry(expiresAt: string | null): string | null {
  if (!expiresAt) return null;
  const date = new Date(expiresAt);
  return `Expires - ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function isNewDeal(deal: Deal): boolean {
  if (!deal.effective_at) return false;
  const diffMs = Date.now() - new Date(deal.effective_at).getTime();
  return diffMs < 24 * 60 * 60 * 1000;
}

export const DealCard = ({ deal }: Props) => {
  const expiry = formatExpiry(deal.expires_at);
  const isNew = isNewDeal(deal);
  const dealClickHref =
    `/deal-click/${encodeURIComponent(deal.retailer)}/${encodeURIComponent(deal.linkTemplate ?? deal.link)}/web` +
    (deal.wrapAffil ? '?wrapAffil=true' : '');

  return (
    <div
      className='flex flex-col items-end gap-4 overflow-hidden rounded-2xl p-6'
      style={{ backgroundColor: isNew ? '#F1B820' : '#FBE9BA' }}
    >
      {/* Top row: icon column + content column */}
      <div className='flex w-full items-start gap-6'>
        {/* Left column: tag + retailer icon */}
        <div className='flex shrink-0 flex-col items-start gap-2'>
          {isNew && (
            <span className='font-poppins w-full rounded bg-primary-off px-4 py-2 text-center text-sm font-semibold text-[#ECECF2]'>
              New!
            </span>
          )}
          <div
            className='shrink-0 overflow-hidden rounded-xl bg-white'
            style={{
              width: 72,
              height: 72,
              minWidth: 72,
              minHeight: 72,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {deal.image_url ? (
              <img
                src={deal.image_url}
                alt={deal.retailerDisplayName ?? deal.retailer}
                style={{ width: 56, height: 56, objectFit: 'contain' }}
              />
            ) : (
              <span className='text-2xl font-bold text-primary-dark'>
                {(deal.retailerDisplayName ?? deal.retailer).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {/* Right column: title, description, expiry */}
        <div className='flex flex-1 flex-col gap-4'>
          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-1'>
              {deal.retailerDisplayName && (
                <p className='font-poppins text-xs font-semibold uppercase tracking-widest text-[#8b6914]'>
                  {deal.retailerDisplayName}
                </p>
              )}
              <h3 className='heading-300 !text-[#302404]'>{deal.name}</h3>
            </div>
            {deal.description && <p className='text-300 !text-[#3c2c04]'>{deal.description}</p>}
          </div>

          {deal.code && (
            <p className='font-openSans text-sm text-[#3c2c04]'>
              Code: <span className='font-semibold'>{deal.code}</span>
            </p>
          )}

          {expiry && (
            <span className='font-poppins w-fit rounded bg-[#f6d06d] px-2 py-1 text-sm font-medium text-[#3c2c04]'>
              {expiry}
            </span>
          )}
        </div>
      </div>

      {/* CTA button — bottom right */}
      <a
        href={dealClickHref}
        target='_blank'
        rel='sponsored nofollow noopener noreferrer'
        className='button-primary flex h-12 shrink-0 items-center gap-2'
        onClick={() => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'Deals',
              eventCategory: 'engagement',
              eventLabel: 'Deal Clicked',
              eventValue: {
                dealName: deal.name,
                vendor: deal.retailer,
                dealLink: deal.link,
              },
            },
          });
        }}
      >
        Get Offer
        <ArrowForwardIcon style={{ fontSize: '18px' }} />
      </a>
    </div>
  );
};
